import {
  siBehance,
  siDiscord,
  siDribbble,
  siGithub,
  siInstagram,
  siX,
  siYoutube,
} from "simple-icons";

import { cn } from "@/lib/utils";

import { MaltIcon } from "./malt-icon";

// =============================================================================

const SIMPLE_ICONS = {
  github: siGithub,
  x: siX,
  instagram: siInstagram,
  dribbble: siDribbble,
  behance: siBehance,
  youtube: siYoutube,
  discord: siDiscord,
} as const;

export const BRAND_ICON_KEYS = [
  "linkedin",
  "malt",
  ...Object.keys(SIMPLE_ICONS),
] as Array<"linkedin" | "malt" | keyof typeof SIMPLE_ICONS>;

export type BrandIconKey = (typeof BRAND_ICON_KEYS)[number];

export function isBrandIconKey(value: unknown): value is BrandIconKey {
  return (
    typeof value === "string" && (BRAND_ICON_KEYS as string[]).includes(value)
  );
}

type BrandIconProps = {
  name: BrandIconKey;
  className?: string;
  title?: string;
};

/**
 * Monochrome brand logo of a network (uses currentColor). LinkedIn and Malt
 * are drawn by hand: simple-icons has no LinkedIn logo and only the Malt
 * wordmark.
 */
export function BrandIcon({ name, className, title }: BrandIconProps) {
  const a11y = title
    ? { role: "img" as const, "aria-label": title }
    : { "aria-hidden": true };

  if (name === "malt") {
    return <MaltIcon className={cn("size-6", className)} a11y={a11y} />;
  }

  if (name === "linkedin") {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={cn("size-6", className)}
        {...a11y}
      >
        <rect x="2.5" y="2.5" width="19" height="19" rx="3" />
        <path d="M7.5 10.5v6" />
        <path d="M11.5 16.5v-6" />
        <path d="M11.5 13.25c0-1.6 1-2.75 2.5-2.75s2.5 1.15 2.5 2.75v3.25" />
        <circle cx="7.5" cy="7.6" r="0.4" fill="currentColor" />
      </svg>
    );
  }

  const icon = SIMPLE_ICONS[name];
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("size-6", className)}
      {...a11y}
    >
      <path d={icon.path} />
    </svg>
  );
}

// =============================================================================
